import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { medicineApi } from 'src/api/medicine';
import { warehouseApi } from 'src/api/warehouse';
import { useDebounce } from 'src/hooks/useDebounce';
import { useWarehouseQuery } from 'src/sections/@dashboard/warehouse/hooks/useWarehouseQuery';
import { warehouseRequestOptions } from 'src/sections/@dashboard/warehouse/utils/request';
import { GetMedicineDto } from 'woofwoof-api';
import {
  MedicineSearchOption,
  registryOptions,
  toMedicineSearchOptions,
} from './medicineSearchOptions';

const SEARCH_DEBOUNCE_MS = 300;
/** Below two characters the registry returns noise, so nothing is asked. */
const MIN_QUERY_LENGTH = 2;
const EMPTY_QUERY_LIMIT = 5;
const QUERY_LIMIT = 20;
const REGISTRY_STALE_MS = 5 * 60 * 1000;
const NO_OPTIONS: MedicineSearchOption[] = [];

export interface MedicineSearchOptionsArgs {
  /** What the vet typed; debounced here. */
  query: string;
  /** A Facility form with the Warehouse enabled: the clinic's drugs lead (#690). */
  warehouseAware: boolean;
}

export interface MedicineSearchOptionsResult {
  options: MedicineSearchOption[];
  isLoading: boolean;
}

async function searchRegistry(term: string): Promise<GetMedicineDto[]> {
  const response = await medicineApi.medicineControllerSearchMedicines(
    term
      ? { query: term, limit: QUERY_LIMIT }
      : { limit: EMPTY_QUERY_LIMIT, skip: 0 },
  );
  return response.data.medicines ?? [];
}

/**
 * The one medicine search of the medication form and the treatment lines.
 * Off the Warehouse it is the registry search it always was; on a Facility
 * form it asks `GET api/warehouse/medicine-search` under an organization-scoped
 * key, so switching clinics never shows another clinic's stock.
 */
export function useMedicineSearchOptions({
  query,
  warehouseAware,
}: MedicineSearchOptionsArgs): MedicineSearchOptionsResult {
  const term = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const searchable = term.length === 0 || term.length >= MIN_QUERY_LENGTH;

  const registry = useQuery(
    ['medicine-search', 'registry', term],
    () => searchRegistry(term),
    { enabled: !warehouseAware && searchable, staleTime: REGISTRY_STALE_MS },
  );
  const warehouse = useWarehouseQuery(
    'warehouse-medicine-search',
    [term],
    async (organizationId, signal) =>
      (
        await warehouseApi.warehouseMedicineSearchControllerSearch(
          { query: term || undefined, limit: QUERY_LIMIT },
          warehouseRequestOptions(organizationId, signal),
        )
      ).data,
    { enabled: warehouseAware && searchable },
  );

  const warehouseOptions = useMemo(
    () =>
      warehouse.data ? toMedicineSearchOptions(warehouse.data) : NO_OPTIONS,
    [warehouse.data],
  );
  const registryOnly = useMemo(
    () => (registry.data ? registryOptions(registry.data) : NO_OPTIONS),
    [registry.data],
  );

  if (!searchable) {
    return { options: NO_OPTIONS, isLoading: false };
  }
  return warehouseAware
    ? { options: warehouseOptions, isLoading: warehouse.isInitialLoading }
    : { options: registryOnly, isLoading: registry.isInitialLoading };
}
